import { useEffect, useState } from "react";
import { NavLink } from "react-router";

import type { ApiError, PasswordComplexity } from "@sperrdatei/dto/types";
import type { FormEvent } from "react";

const PasswordPolicyConditions = [
  "minLength",
  "hasUppercase",
  "hasLowercase",
  "hasNumber",
  "hasSymbol",
] as const;

type PasswordPolicyCondition = (typeof PasswordPolicyConditions)[number];

type ConditionCheckProps = {
  condition: PasswordPolicyCondition;
  passwordPolicy: PasswordComplexity | undefined;
  password: string | undefined;
};

const checkPasswordPolicyCondition = (
  condition: PasswordPolicyCondition,
  policy: PasswordComplexity | undefined,
  currentPassword: string | undefined,
): boolean => {
  if (!policy || !currentPassword) {
    return false;
  }

  switch (condition) {
    case "minLength":
      return currentPassword.length >= policy.minLength;
    case "hasLowercase":
      return policy.hasLowercase ? /[a-z]/.test(currentPassword) : true;
    case "hasUppercase":
      return policy.hasUppercase ? /[A-Z]/.test(currentPassword) : true;
    case "hasNumber":
      return policy.hasUppercase ? /[0-9]/.test(currentPassword) : true;
    case "hasSymbol":
      return policy.hasUppercase
        ? /[^a-zA-Z0-9\s]/.test(currentPassword)
        : true;
  }
};

const ConditionCheck: React.FC<ConditionCheckProps> = ({
  condition,
  passwordPolicy,
  password,
}: ConditionCheckProps) => {
  return checkPasswordPolicyCondition(condition, passwordPolicy, password) ? (
    <i className="bi bi-check-square condition-success" />
  ) : (
    <i className="bi bi-x-square" />
  );
};

export type ChangePasswordProps = {
  successTitle: string;
  successMessage: string;
  submitAction: (password: string) => Promise<ApiError | undefined>;
  termsOfServiceAccepted?: boolean;
  openTermsOfService?: () => void;
};

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  successTitle,
  successMessage,
  termsOfServiceAccepted,
  openTermsOfService,
  submitAction,
}: ChangePasswordProps) => {
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [password, setPassword] = useState<string | undefined>();
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>();

  const [passwordPolicy, setPasswordPolicy] = useState<
    PasswordComplexity | undefined
  >();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<ApiError | null>();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!password) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    const error = await submitAction(password);

    if (error) {
      setError(error);
    } else {
      setSuccess(true);
    }
  };

  useEffect(() => {
    fetch("/api/user/password/policy")
      .then((response) => {
        if (response.ok) {
          response
            .json()
            .then((policy: PasswordComplexity) => {
              setPasswordPolicy(policy);
            })
            .catch(() => {
              setError({
                code: "UNKNOWN_ERROR",
                message:
                  "Ein unerwarteter Fehler ist aufgetreten, bitte wenden Sie sich an den Administrator",
              });
            });
        }
      })
      .catch((error: ApiError) => {
        setError(error);
      });
  }, []);

  useEffect(() => {
    const isPolicyValid = PasswordPolicyConditions.every((condition) =>
      checkPasswordPolicyCondition(condition, passwordPolicy, password),
    );
    const isPasswordConfirmed = password === confirmPassword;

    setIsPasswordValid(isPolicyValid && isPasswordConfirmed && !error);
  }, [password, confirmPassword, passwordPolicy, error]);

  return !success ? (
    <div className="login container d-flex flex-column justify-content-center align-items-center h-100">
      <form onSubmit={handleSubmit} className="container border p-4">
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <h6 className="alert-heading small">
              <i className="bi bi-exclamation-triangle-fill" /> Fehler
            </h6>
            <span className="small">{error.message}</span>
          </div>
        )}
        <div className="mb-3">
          <label htmlFor="password" className="form-text">
            Passwort
          </label>
          <div className="input-group">
            <input
              id="password"
              type="password"
              value={password}
              className="form-control"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div id="passwordFeedback" className="invalid-feedback">
              Falsches Passwort
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label htmlFor="confirmPassword" className="form-text">
            Passwort Bestätigen
          </label>
          <div className="input-group">
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              className="form-control"
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <div id="passwordFeedback" className="invalid-feedback">
              Falsches Passwort
            </div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <ConditionCheck
              condition="minLength"
              passwordPolicy={passwordPolicy}
              password={password}
            />
            <span className="small text-muted">
              {" "}
              Passwortlänge ({passwordPolicy?.minLength} Zeichen)
            </span>
          </div>
          <div className="col-6">
            {!!password && password === confirmPassword ? (
              <i className="bi bi-check-square condition-success" />
            ) : (
              <i className="bi bi-x-square" />
            )}
            <span className="small text-muted"> Passwortübereinstimmung</span>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <ConditionCheck
              condition="hasNumber"
              passwordPolicy={passwordPolicy}
              password={password}
            />
            <span className="small text-muted"> Zahlen</span>
          </div>
          <div className="col-6">
            <ConditionCheck
              condition="hasSymbol"
              passwordPolicy={passwordPolicy}
              password={password}
            />
            <span className="small text-muted"> Sonderzeichen</span>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <ConditionCheck
              condition="hasUppercase"
              passwordPolicy={passwordPolicy}
              password={password}
            />
            <span className="small text-muted"> Großbuchstaben</span>
          </div>
          <div className="col-6">
            <ConditionCheck
              condition="hasLowercase"
              passwordPolicy={passwordPolicy}
              password={password}
            />
            <span className="small text-muted"> Kleinbuchstaben</span>
          </div>
        </div>
        {termsOfServiceAccepted !== undefined ? (
          <div className="row mb-4">
            <div className="col-12">
              {termsOfServiceAccepted ? (
                <i className="bi bi-check-square condition-success" />
              ) : (
                <i className="bi bi-x-square" />
              )}
              <span className="small text-muted">
                {" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (openTermsOfService) openTermsOfService();
                    setError(null);
                  }}
                >
                  Datenschutzerklärung
                </a>
              </span>
            </div>
          </div>
        ) : null}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!isPasswordValid}
        >
          Weiter
        </button>
      </form>
    </div>
  ) : (
    <div className="center-message container d-flex flex-column justify-content-center h-100">
      <h5>{successTitle}</h5>
      <p>
        {successMessage} <NavLink to="/">anmelden</NavLink>.
      </p>
    </div>
  );
};
